import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  CardMedia,
  Paper,
  Container,
} from "@mui/material";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";
import ForgotMpin from "../WebSite/ForgotMpin";
import logo from "../Assets/iPaisaLogo/RockkpayLogo.png";
import { MadeInIndiaIcon } from "../../res/icons";

const validationSchema = Yup.object({
  mobileNumber: Yup.string()
    .required("Mobile Number is required")
    .matches(/^[0-9]+$/, "Mobile number must be digits only")
    .length(10, "Mobile number must be exactly 10 digits"),
  mpin: Yup.string()
    .required("MPIN is required")
    .matches(/^[0-9]{0,4}$/, "MPIN must be 0-4 digits long")
    .max(4, "MPIN must be at most 4 digits long"),
});

const setMpinSchema = Yup.object({
  newMpin: Yup.string()
    .required("New MPIN is required")
    .matches(/^[0-9]{0,4}$/, "MPIN must be 0-4 digits long")
    .max(4, "MPIN must be at most 4 digits long"),
  confirmMpin: Yup.string()
    .oneOf([Yup.ref("newMpin"), null], "MPINs must match")
    .required("Confirm MPIN is required"),
});

const SignIn = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isMobileValid, setIsMobileValid] = useState(null);
  const [showForgotMpin, setShowForgotMpin] = useState(false);
  const [showSetMpin, setShowSetMpin] = useState(false);
  const [mobileNumber, setMobileNumber] = useState("");
  const [setMpin, setSetMpin] = useState(null);
  const navigate = useNavigate();

  const handleForgotMpinSuccess = () => {
    setShowForgotMpin(false);
    toast.success("Now You Can Login!");
    navigate("/signin");
  };

  useEffect(() => {
    if (isMobileValid === true) {
      toast.success("Mobile number is valid");
    } else if (isMobileValid === false) {
      // toast.error("Mobile number is already registered");
    }
  }, [isMobileValid]);

  const handleLogin = useCallback(
    async (values, { setSubmitting }) => {
      setIsSubmitting(true);
      try {
        const response = await axios.post(
          "https://api.ipaisa.co.in/myapp/auth/signin",
          values
        );

        // Check for 'Bad Credentials' response
        if (
          response.data.success === false &&
          response.data.message === "Bad Credentials"
        ) {
          toast.error("Invalid mobile number or MPIN. Please try again.");
          return; // Prevent navigation to dashboard
        }

        // Check for 'INACTIVE' status
        if (response.data.status === "INACTIVE") {
          toast.error("Your account is inactive. Please contact support.");
          return; // Prevent navigation to dashboard
        }

        // Proceed with setting items in sessionStorage and navigating if login is successful
        sessionStorage.setItem("jwtToken", response.data.token);
        sessionStorage.setItem(
          "cmsMerchantApplyStatus",
          response.data.cmsMerchantApplyStatus
        );
        sessionStorage.setItem("uType", response.data.userType);
        sessionStorage.setItem(
          "username",
          response.data.firstName + " " + response.data.lastName
        );
        sessionStorage.setItem("bulkPayout", response.data.bulkPayout);
        sessionStorage.setItem("payoutStatus", response.data.payoutStatus);
        sessionStorage.setItem("mobileNumber", values.mobileNumber);
        sessionStorage.setItem("mpin", values.mpin);
        sessionStorage.setItem("userId", response.data.userId);
        sessionStorage.setItem("status", response.data.status);
        sessionStorage.setItem("isTxnMpinSet", response.data.isTxnMpinSet);
        sessionStorage.setItem("commORSur", response.data.commORSur);
        sessionStorage.setItem("firstname", response.data.firstName);
        sessionStorage.setItem("lastname", response.data.lastName);
        sessionStorage.setItem("payinService", response.data.payinService);
        sessionStorage.setItem("payoutService", response.data.payoutService);

        // Only call WhatsApp API if isFirstLogin is true
        if (response.data.isFirstLogin === "TRUE") {
          await axios.post(
            "https://api.ipaisa.co.in/myapp/auth/whatsapp/msg",
            {
              to: values.mobileNumber,
              text1: response.data.firstName + " " + response.data.lastName,
              text2: values.mobileNumber,
              text3: values.mpin,
            }
          );
        }

        // Check if the userType is SUBADMIN, then trigger the second API call
        if (response.data.userType === "SUBADMIN") {
          const myAccessResponse = await axios.get(
            "https://api.ipaisa.co.in/myapp/auth/myaccess",
            {
              headers: {
                Authorization: `Bearer ${response.data.token}`,
              },
            }
          );

          // Store only the properties that are true
          const myAccessData = myAccessResponse.data;

          // Store truthy values from myAccessData
          if (myAccessData.ftr)
            sessionStorage.setItem("Fund Transfer Report", myAccessData.ftr);
          if (myAccessData.qqr)
            sessionStorage.setItem("Queen QR Trans Report", myAccessData.qqr);
          if (myAccessData.kqr)
            sessionStorage.setItem("King QR Trans Report", myAccessData.kqr);
          if (myAccessData.cc)
            sessionStorage.setItem("Credit Card Report", myAccessData.cc);
          if (myAccessData.ctr)
            sessionStorage.setItem(
              "Completed Transaction Report",
              myAccessData.ctr
            );
          if (myAccessData.rmr)
            sessionStorage.setItem("Reverse Money Report", myAccessData.rmr);
          if (myAccessData.cr)
            sessionStorage.setItem("Commission Report", myAccessData.cr);
          if (myAccessData.hwr)
            sessionStorage.setItem("Hierarchy Wise Report", myAccessData.hwr);
          if (myAccessData.pr)
            sessionStorage.setItem("Pending Report", myAccessData.pr);
          if (myAccessData.tpayouts)
            sessionStorage.setItem(
              "Total Payout Report",
              myAccessData.tpayouts
            );
          if (myAccessData.fundTransfer)
            sessionStorage.setItem("Fund Transfer", myAccessData.fundTransfer);
          if (myAccessData.reverseMoney)
            sessionStorage.setItem("Reverse Money", myAccessData.reverseMoney);
          if (myAccessData.activateDeactivateAll)
            sessionStorage.setItem(
              "ActivateDeactivateAll",
              myAccessData.activateDeactivateAll
            );
          if (myAccessData.automatePayout)
            sessionStorage.setItem(
              "AutomatePayout",
              myAccessData.automatePayout
            );
          if (myAccessData.partnersList)
            sessionStorage.setItem("Partners List", myAccessData.partnersList);
          if (myAccessData.manualPayout)
            sessionStorage.setItem("Manual Payout", myAccessData.manualPayout);
          if (myAccessData.manualApproved)
            sessionStorage.setItem(
              "Manual Approved",
              myAccessData.manualApproved
            );

          if (myAccessData.paymentServices)
            sessionStorage.setItem(
              "Payment Services",
              myAccessData.paymentServices
            );
          if (myAccessData.userServices)
            sessionStorage.setItem("User Services", myAccessData.userServices);

          // Optionally store user info from the 'user' field in the myAccess response if they are truthy
          const user = myAccessData.user;
          if (user.userid) sessionStorage.setItem("subAdminId", user.userid);
          if (user.firstName)
            sessionStorage.setItem("subAdminFirstName", user.firstName);
          if (user.lastName)
            sessionStorage.setItem("subAdminLastName", user.lastName);
          if (user.email) sessionStorage.setItem("subAdminEmail", user.email);
          if (user.mobileNumber)
            sessionStorage.setItem("subAdminMobileNumber", user.mobileNumber);
          if (user.status)
            sessionStorage.setItem("subAdminStatus", user.status);
          if (user.payoutStatus)
            sessionStorage.setItem("subAdminPayoutStatus", user.payoutStatus);
          if (user.walletBalance)
            sessionStorage.setItem("subAdminWalletBalance", user.walletBalance);
          if (user.payoutService)
            sessionStorage.setItem("subAdminPayoutService", user.payoutService);
          if (user.payinService)
            sessionStorage.setItem("subAdminPayinService", user.payinService);
          if (user.isFirstLogin)
            sessionStorage.setItem("subAdminIsFirstLogin", user.isFirstLogin);
        }

        // toast.success("Login successful");
        navigate("/dashboard");
      } catch (error) {
        console.error("Error logging in:", error);
        toast.error("Enter valid Mobile Number or Mpin");
      } finally {
        setIsSubmitting(false);
        setSubmitting(false);
      }
    },
    [navigate, setMpin]
  );

  const validatemobileno = useCallback(async (mobile) => {
    try {
      const response = await axios.post(
        "https://api.ipaisa.co.in/myapp/auth/validatemobilenumber",
        { mobileno: mobile }
      );

      const isValidMobile = response.data.mobileValidate;
      setIsMobileValid(isValidMobile);

      if (isValidMobile) {
        setSetMpin(response.data.setMpin); // Store the setMpin value for later use

        if (response.data.setMpin === false) {
          setShowSetMpin(true); // Prompt to set MPIN
        } else {
          setShowSetMpin(false); // Proceed to login with MPIN
        }
      } else {
        setShowSetMpin(false); // Mobile number not registered
      }
    } catch (error) {
      console.error("Error validating mobile number:", error);
      setIsMobileValid(false);
    }
  }, []);

  const handleSetMpin = useCallback(
    async (values, { setSubmitting }) => {
      setIsSubmitting(true);
      try {
        const response = await axios.post(
          "https://api.ipaisa.co.in/myapp/auth/setmpin",
          { mobileno: mobileNumber, mpin: values.newMpin }
        );
        if (response.data.success) {
          toast.success("MPIN set successfully");
          setShowSetMpin(false);
          navigate("/signin"); // Redirect to sign-in page
        }
      } catch (error) {
        console.error("Error setting MPIN:", error);
        toast.error("Error setting MPIN");
      } finally {
        setIsSubmitting(false);
        setSubmitting(false);
      }
    },
    [mobileNumber, navigate]
  );

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "linear-gradient(135deg, #f0f4ff 0%, #e0e7ff 100%)",
      }}
    >
      <ToastContainer />
      <Paper
        elevation={6}
        sx={{
          p: { xs: 2.5, sm: 4 },
          borderRadius: 4,
          minWidth: 300,
          maxWidth: 370,
          width: "100%",
          boxShadow: "0 8px 32px 0 rgba(31, 38, 135, 0.15)",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          my: { xs: 2, sm: 4 }, // vertical margin for small screens
        }}
      >
        <CardMedia
          component="img"
          image={logo}
          alt="RockkPay Logo"
          sx={{
            height: 70,
            width: 70,
            borderRadius: "50%",
            mb: 2,
            boxShadow: "0 2px 8px 0 rgba(31, 38, 135, 0.10)",
            background: "#fff",
          }}
        />
        <Typography
          variant="h5"
          sx={{
            fontWeight: 700,
            color: "#0078FF",
            mb: 1,
            textAlign: "center",
            letterSpacing: 1,
          }}
        >
          Sign In
        </Typography>
        <Typography
          variant="subtitle1"
          sx={{ color: "#343C6A", mb: 3, textAlign: "center" }}
        >
          Welcome to RockkPay
        </Typography>
        {showForgotMpin ? (
          <ForgotMpin
            handleMpinSuccess={() => {
              setShowForgotMpin(false);
              toast.success("Now You Can Login!");
              navigate("/signin");
            }}
          />
        ) : showSetMpin ? (
          <SetMpinForm
            handleSetMpin={handleSetMpin}
            isSubmitting={isSubmitting}
          />
        ) : (
          <SignInForm
            handleLogin={handleLogin}
            validatemobileno={validatemobileno}
            isSubmitting={isSubmitting}
            isMobileValid={isMobileValid}
            setShowForgotMpin={setShowForgotMpin}
            setMobileNumber={setMobileNumber}
          />
        )}
        <Box
          sx={{
            mt: 4,
            width: "100%",
            display: "flex",
            justifyContent: "center",
          }}
        >
          <MadeInIndiaIcon style={{ width: 60, height: "auto" }} />
        </Box>
      </Paper>
    </Box>
  );
};

const SignInForm = ({
  handleLogin,
  validatemobileno,
  isSubmitting,
  isMobileValid,
  setShowForgotMpin,
  setMobileNumber,
}) => (
  <Formik
    initialValues={{ mobileNumber: "", mpin: "" }}
    validationSchema={validationSchema}
    onSubmit={handleLogin}
  >
    {({ errors, touched, setFieldValue }) => {
      const handleInputChange = (e, field, maxLength) => {
        const value = e.target.value;
        const numericValue = value.replace(/\D/g, "");
        if (numericValue.length <= maxLength) {
          setFieldValue(field, numericValue);
          if (field === "mobileNumber") {
            setMobileNumber(numericValue);
            if (numericValue.length === 10) {
              validatemobileno(numericValue);
            }
          }
        }
      };
      return (
        <Form style={{ width: "100%" }} autoComplete="off">
          <Field
            as={TextField}
            variant="outlined"
            margin="normal"
            required
            fullWidth
            id="mobileNumber"
            label="Mobile Number"
            name="mobileNumber"
            autoComplete="off"
            autoFocus
            InputLabelProps={{ shrink: true }}
            helperText={<ErrorMessage name="mobileNumber" />}
            error={touched.mobileNumber && Boolean(errors.mobileNumber)}
            onChange={(e) => handleInputChange(e, "mobileNumber", 10)}
            sx={{ mb: 2, borderRadius: 2, background: "#f8faff" }}
          />
          <Field
            as={TextField}
            variant="outlined"
            margin="normal"
            required
            fullWidth
            name="mpin"
            label="MPIN"
            type="password"
            id="mpin"
            autoComplete="off"
            InputLabelProps={{ shrink: true }}
            helperText={<ErrorMessage name="mpin" />}
            error={touched.mpin && Boolean(errors.mpin)}
            onChange={(e) => handleInputChange(e, "mpin", 4)}
            sx={{ mb: 1.5, borderRadius: 2, background: "#f8faff" }}
          />
          <Box
            display="flex"
            justifyContent="flex-end"
            width="100%"
            marginBottom="2%"
          >
            <Button
              style={{
                color: "#0078FF",
                textTransform: "none",
                fontWeight: 600,
              }}
              onClick={() => setShowForgotMpin(true)}
            >
              Forgot MPIN?
            </Button>
          </Box>
          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{
              height: 48,
              background: "linear-gradient(90deg, #0078FF 0%, #0056b3 100%)",
              borderRadius: 2,
              fontWeight: 700,
              textTransform: "none",
              fontSize: 18,
              boxShadow: "0 2px 8px 0 rgba(31, 38, 135, 0.10)",
              mt: 1,
            }}
            disabled={isSubmitting || isMobileValid === false}
          >
            Login
          </Button>
        </Form>
      );
    }}
  </Formik>
);

const SetMpinForm = ({ handleSetMpin, isSubmitting }) => (
  <Formik
    initialValues={{ newMpin: "", confirmMpin: "" }}
    validationSchema={setMpinSchema}
    onSubmit={handleSetMpin}
  >
    {({ errors, touched, setFieldValue }) => {
      const handleInputChange = (e, field, maxLength) => {
        const value = e.target.value;
        const numericValue = value.replace(/\D/g, "");
        if (numericValue.length <= maxLength) {
          setFieldValue(field, numericValue);
        }
      };
      return (
        <Form style={{ width: "100%" }} autoComplete="off">
          <Field
            as={TextField}
            variant="outlined"
            margin="normal"
            required
            fullWidth
            id="newMpin"
            label="New MPIN"
            name="newMpin"
            type="password"
            autoComplete="off"
            InputLabelProps={{ shrink: true }}
            helperText={<ErrorMessage name="newMpin" />}
            error={touched.newMpin && Boolean(errors.newMpin)}
            onChange={(e) => handleInputChange(e, "newMpin", 4)}
            sx={{ mb: 2, borderRadius: 2, background: "#f8faff" }}
          />
          <Field
            as={TextField}
            variant="outlined"
            margin="normal"
            required
            fullWidth
            id="confirmMpin"
            label="Confirm MPIN"
            name="confirmMpin"
            type="password"
            autoComplete="off"
            InputLabelProps={{ shrink: true }}
            helperText={<ErrorMessage name="confirmMpin" />}
            error={touched.confirmMpin && Boolean(errors.confirmMpin)}
            onChange={(e) => handleInputChange(e, "confirmMpin", 4)}
            sx={{ mb: 2, borderRadius: 2, background: "#f8faff" }}
          />
          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{
              height: 48,
              background: "linear-gradient(90deg, #0078FF 0%, #0056b3 100%)",
              borderRadius: 2,
              fontWeight: 700,
              textTransform: "none",
              fontSize: 18,
              boxShadow: "0 2px 8px 0 rgba(31, 38, 135, 0.10)",
              mt: 1,
            }}
            disabled={isSubmitting}
          >
            Set MPIN
          </Button>
        </Form>
      );
    }}
  </Formik>
);

export default SignIn;
